import { useEffect, useMemo, useRef, useState } from "react";
import { Check, ChevronDown, ChevronsUpDown, CircleCheck } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface SearchableSelectOption {
  label: string;
  searchText?: string;
  value: string;
}

interface SearchableSelectProps {
  ariaLabel: string;
  emptyMessage: string;
  onValueChange: (value: string) => void;
  options: SearchableSelectOption[];
  placeholder: string;
  searchPlaceholder: string;
  searchable?: boolean;
  value: string;
}

export const SearchableSelect = ({
  ariaLabel,
  emptyMessage,
  onValueChange,
  options,
  placeholder,
  searchPlaceholder,
  searchable = true,
  value,
}: SearchableSelectProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  const selectedOption = options.find((option) => option.value === value);
  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) return options;

    return options.filter((option) =>
      `${option.label} ${option.searchText ?? ""}`
        .toLowerCase()
        .includes(normalizedQuery),
    );
  }, [options, query]);

  useEffect(() => {
    if (!open) return;

    const frame = requestAnimationFrame(() => {
      listRef.current?.scrollTo({ top: 0 });
    });

    return () => cancelAnimationFrame(frame);
  }, [open]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) setQuery("");
  };

  const handleSelect = (nextValue: string) => {
    onValueChange(nextValue);
    setQuery("");
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          aria-expanded={open}
          aria-label={ariaLabel}
          className="flex h-12 w-full items-center justify-between rounded-lg border border-[#efefef] bg-transparent px-4 text-left text-base shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)] focus-visible:ring-offset-2"
          role="combobox"
          type="button"
        >
          <span className={cn("truncate", !selectedOption && "text-muted-foreground")}>
            {selectedOption?.label ?? placeholder}
          </span>
          {searchable ? (
            <ChevronsUpDown aria-hidden="true" className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          ) : (
            <ChevronDown
              aria-hidden="true"
              className="ml-2 h-6 w-6 shrink-0 text-[#5b616d] transition-transform data-[open=true]:rotate-180"
              data-open={open}
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          "w-[var(--radix-popover-trigger-width)] overflow-hidden bg-white",
          searchable
            ? "border-[#dddfe4] p-0"
            : "rounded-2xl border-0 p-3 shadow-[0_4px_6px_rgba(0,0,0,0.05),0_10px_15px_rgba(0,0,0,0.10)]",
        )}
        collisionPadding={16}
        sideOffset={6}
      >
        <Command shouldFilter={false}>
          {searchable && (
            <CommandInput
              className="h-12 text-base"
              onValueChange={setQuery}
              placeholder={searchPlaceholder}
              value={query}
            />
          )}
          <CommandList
            className={cn(searchable ? "max-h-[360px] p-2" : "max-h-[373px] p-0")}
            ref={listRef}
          >
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup className="p-0">
              {filteredOptions.map((option) => (
                <CommandItem
                  className={cn(
                    "text-base",
                    searchable
                      ? "py-3"
                      : "rounded-none border-b border-[#f4f4f6] p-4 font-medium last:border-b-0",
                    !searchable && value === option.value && "rounded-2xl border border-[var(--dp-brand)] bg-[var(--dp-brand-soft)]",
                  )}
                  key={option.value}
                  onSelect={() => handleSelect(option.value)}
                  value={option.value}
                >
                  <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  {searchable ? (
                    <Check
                      aria-hidden="true"
                      className={cn(
                        "ml-2 h-4 w-4 shrink-0",
                        value === option.value ? "opacity-100" : "opacity-0",
                      )}
                    />
                  ) : value === option.value ? (
                    <CircleCheck aria-hidden="true" className="ml-2 h-5 w-5 shrink-0 text-[var(--dp-brand)]" />
                  ) : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
